import type { FieldValues } from 'react-hook-form';
import type { DefaultValues } from 'react-hook-form';
import type { JSX } from 'react';
import { DynamicForm } from '../DynamicForm';
import { useDataManagementContext } from '../../../hooks/useDataManagementContext';
import { useRelatedDataWithContext } from '../../../hooks/useRelatedDataWithContext';
import { useErrorHandling } from '../../../utils/useErrorHandling';
import { Logger } from '@shared/logger';
import { tryCatchAsync } from '@shared/utils';

export function DataManagementForm<TData extends FieldValues>(): JSX.Element {
  const context = useDataManagementContext<TData>();
  const { showContextError, showErrorToast } = useErrorHandling();

  if (!context) {
    showContextError('DataManagementForm');
    return <div>Error: DataManagementForm context not available</div>;
  }

  const { 
    mappings, 
    schema, 
    selectedItem, 
    onSubmit, 
    onOpenChange 
  } = context;

  // Fetch related data for the form
  const { relatedData, isLoading: relatedDataLoading, error: relatedDataError } = useRelatedDataWithContext(mappings);

  // Show error if related data failed to load
  if (relatedDataError) {
    Logger.warn('Related data loading error in form', { resource: 'data.users', error: relatedDataError });
  }

  return (
    <DynamicForm<TData>
      mappings={mappings}
      schema={schema}
      defaultValues={selectedItem as DefaultValues<TData> || undefined}
      onSubmit={async (data): Promise<void> => {
        const result = await tryCatchAsync(async () => {
          await onSubmit(data);
          onOpenChange(false);
        });

        if (result.isErr) {
          const error = result.getFail();
          Logger.error('Error in form submission', { resource: 'data.users' }, error instanceof Error ? error : new Error(String(error)));
          showErrorToast(error, 'Form Submission Error');
        }
      }}
      isEdit={!!selectedItem}
      onCancel={() => onOpenChange(false)}
      relatedData={relatedData}
      isLoading={relatedDataLoading}
    />
  );
} 